/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { CheckboxDropdown } from 'modules/common/components';
import { TAGS } from 'modules/transactions/constants';
import { selectBreakdownDetails, selectVisualFilters } from 'modules/scenario/selectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSelectedBreakdown, getFocusOnFilter } from 'modules/scenario/function';
import { getSubBreakdownFilter } from 'modules/transactions/components/inner-filter/functions';

const FocusOnFilter = ({ value, onChange }) => {
  const breakdownData = useSelector(selectBreakdownDetails);
  const visualFilters = useSelector(selectVisualFilters);

  const [chartBreakdownList, setChartBreakdownList] = useState([]);
  const [focusOnList, setFocusOnList] = useState([]);
  const [chartBreakdown, setChartBreakdown] = useState(value?.breakdown ?? null);

  const handleBreakdownChange = (event) => {
    const selectedLabel = event.target.value;
    setChartBreakdown(selectedLabel);
  };
  //
  const handleFocusOnChange = (event, val) => {
    const values = val.map((item) => item.label);
    onChange?.({ breakdown: chartBreakdown, values });
  };
  // Update chartBreakdownList when visualFilters change
  useEffect(() => {
    const subBreakdownFilter = getSubBreakdownFilter(visualFilters, TAGS.TREND);
    if (subBreakdownFilter) {
      setChartBreakdownList(subBreakdownFilter);
    }
  }, [visualFilters]);

  // Update focusOnList when chartBreakdown or chartBreakdownList changes
  useEffect(() => {
    const chart = chartBreakdownList.find((e) => e.label === chartBreakdown);
    if (chart) {
      const breakdown = getSelectedBreakdown(visualFilters, chart.id, false);
      const focusOnFilter = getFocusOnFilter(breakdownData, breakdown);
      setFocusOnList(focusOnFilter);
    } else {
      setFocusOnList([]);
    }
  }, [chartBreakdown, chartBreakdownList, visualFilters, breakdownData]);
  //
  const focusOn = focusOnList.filter((item) => value?.values?.includes?.(item?.label));
  //
  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column', pl: 1 }}>
      <RadioGroup value={chartBreakdown ?? ''} onChange={handleBreakdownChange}>
        {chartBreakdownList
          ?.sort((a, b) => a.label.localeCompare(b.label))
          .map((item) => (
            <FormControlLabel
              key={item?.id}
              value={item?.label}
              control={<Radio />}
              label={item?.label}
              sx={{ my: -0.5 }}
            />
          ))}
      </RadioGroup>

      {focusOnList.length > 0 && (
        <FormControl>
          <CheckboxDropdown
            options={focusOnList}
            values={focusOn}
            label="Focus On"
            onchange={handleFocusOnChange}
            isOptionEqualTo={(option, val) => option?.id === val?.id}
          />
        </FormControl>
      )}
    </Box>
  );
};

export default FocusOnFilter;
